import { BaseListenConfig, BaseStaticConfig, Boolean, Default, IFeatures, Json } from '@engineering11/config-web'

// TODO: Experiment with nesting like-features (e.g. promotions, ai)

export interface ICnectFeatures {
  core: boolean
  portfolio: boolean
  virtualDialogues: boolean
  messaging: boolean
  notifications: boolean
  resumeSharing: boolean
  resumeParsing: boolean
  informationalContent: boolean
  /* Candidate side */
  candidateProfilePDF: boolean
  /* Employer side */
  downloadPDF: boolean
  referrals: boolean
  network: boolean
  communities: boolean
  candidatePool: boolean
  jobApplicationChannels: boolean
  jobSearch: boolean
  promotions: boolean
  promoteResumeParsing: boolean
  employerJobPostPromotions: boolean
  aiSummary: boolean
  aiJobGeneration: boolean
  employerInterviewQuestions: boolean
  employerJobPromotions: boolean
  employerShareApplication: boolean
  employerJobAutomation: boolean
  aiCandidateAnalysis: boolean
  cultureIndex: boolean
  /* Story 2.2a — new-IA flag-gated leaves (R1, mirror of cnect_employer_mobile PR #532 + 2.2b).
   * All default OFF; remote bootstrap JSON flips them ON per environment as surfaces ship. */
  employerDashboard: boolean
  employerCompanyProfile: boolean
  employerOrgAccess: boolean
  employerPersonnelTeam: boolean
  employerOrgCharts: boolean
  employerMessagingChannels: boolean
  employerAiChatSupport: boolean
  employerJobApplicationSettings: boolean
  employerEngageProspects: boolean
  employerPipelineConfig: boolean
  employerMarketingChannels: boolean
  /** Campaigns surface (/campaigns route + nav leaf). Dedicated gate — decoupled from
   * `employerJobPromotions`, which now only gates the legacy /jobs/promotions surface. */
  employerCampaigns: boolean
  /** Email groups surface (/settings/email-groups + its Company settings catalog row).
   * Named lists of email addresses a company can send an opening to; nothing sends email yet.
   * Default OFF; remote bootstrap JSON flips it ON per environment as the backend ships. */
  employerEmailGroups: boolean
  /** Sending an opening to those groups as an email — `employerEmailGroups` gates managing the lists, not sending. */
  /** Internal openings (Visibility): the wizard's Visibility step, the editor's Public / Internal segment,
   * the visibility gate on the careers-page switch and the Internal / Confidential audience chips.
   * Default OFF; with it off every opening behaves as Public, whatever visibility it has stored. */
  employerInternalOpenings: boolean
  /** The in-app assistant (bottom command bar + dock + tour). Gate above the Cnect Plus permission gate:
   * OFF hides every assistant surface; ON renders them, then entitlement decides composer vs upsell.
   * Default ON; set to false per environment to hide it. */
  employerAssistant: boolean
}

export type CnectFeature = keyof ICnectFeatures
export interface CnectFeatures extends ICnectFeatures, IFeatures {}

export const defaultFeatures: ICnectFeatures = {
  core: false,
  portfolio: false,
  virtualDialogues: false,
  messaging: false,
  notifications: false,
  resumeSharing: false,
  resumeParsing: false,
  informationalContent: false,
  candidateProfilePDF: false,
  downloadPDF: false,
  referrals: false,
  network: false,
  communities: false,
  candidatePool: false,
  jobApplicationChannels: false,
  jobSearch: false,
  promotions: false,
  promoteResumeParsing: false,
  employerJobPostPromotions: false,
  aiSummary: false,
  aiJobGeneration: false,
  employerInterviewQuestions: false,
  employerJobPromotions: false,
  employerShareApplication: false,
  employerJobAutomation: false,
  aiCandidateAnalysis: false,
  cultureIndex: false,
  // Story 2.2a defaults (mirror of mobile parity: all OFF in production until verified per surface).
  employerDashboard: false,
  employerCompanyProfile: false,
  employerOrgAccess: false,
  employerPersonnelTeam: false,
  employerOrgCharts: false,
  employerMessagingChannels: false,
  employerAiChatSupport: false,
  employerJobApplicationSettings: false,
  employerEngageProspects: false,
  employerPipelineConfig: false,
  employerMarketingChannels: false,
  employerCampaigns: false,
  employerEmailGroups: false,
  employerInternalOpenings: false,
  employerAssistant: false,
}

export class CnectConfig implements BaseStaticConfig {
  @Boolean()
  @Default(false)
  fromRemoteConfig!: boolean

  @Json()
  @Default(defaultFeatures)
  features!: CnectFeatures
}

export interface CnectListenConfig extends BaseListenConfig {}
